import {
  getV1CompaniesCompanyIdDriverDaysDriverDayIdLocationHistories,
  getV1CompaniesCompanyIdJobsJobIdLocationHistories,
  getV1CompaniesCompanyIdJobsJobIdMovingSitesMovingSiteIdLocationHistories,
  getV1CompaniesCompanyIdLoadsLoadIdLocationHistories,
  getV1CompaniesCompanyIdOrdersOrderIdLocationHistories,
  getV1CompaniesCompanyIdOrdersOrderIdMovingSitesMovingSiteIdLocationHistories,
  type LocationHistory_Read_Minimal,
} from '@treadinc/horizon-api-spec';
import { type AxiosResponse } from 'axios';

import { MAX_LOCATION_HISTORY_PAGE_SIZE } from '~constants/consts';
import { fetchAllApiPages, type PageQuery } from '~utils/fetchAllPages';

import { LocationHistoryMinimal } from './models';

async function fetchAllLocationHistories(
  request: (
    query: PageQuery,
    signal?: AbortSignal,
  ) => Promise<AxiosResponse<{ data: LocationHistory_Read_Minimal[] }>>,
  signal?: AbortSignal,
) {
  try {
    return await fetchAllApiPages(request, LocationHistoryMinimal.parse, {
      pageSize: MAX_LOCATION_HISTORY_PAGE_SIZE,
      signal,
    });
  } catch (error) {
    if (signal?.aborted) throw error;

    console.error('Failed to fetch location histories', error);
    throw error;
  }
}

export function getLocationHistoriesByJobId(
  companyId: string,
  jobId: string,
  signal?: AbortSignal,
): Promise<LocationHistoryMinimal[]> {
  return fetchAllLocationHistories(
    (query, pageSignal) =>
      getV1CompaniesCompanyIdJobsJobIdLocationHistories({
        path: { 'company-id': companyId, 'job-id': jobId },
        query: { 'filter[soft_limit]': false, ...query },
        signal: pageSignal,
      }),
    signal,
  );
}

export function getLocationHistoriesByLoadId(
  companyId: string,
  loadId: string,
  signal?: AbortSignal,
): Promise<LocationHistoryMinimal[]> {
  return fetchAllLocationHistories(
    (query, pageSignal) =>
      getV1CompaniesCompanyIdLoadsLoadIdLocationHistories({
        path: { 'company-id': companyId, 'load-id': loadId },
        query: { 'filter[soft_limit]': false, ...query },
        signal: pageSignal,
      }),
    signal,
  );
}

export function getLocationHistoriesByOrderId(
  companyId: string,
  orderId: string,
  signal?: AbortSignal,
): Promise<LocationHistoryMinimal[]> {
  return fetchAllLocationHistories(
    (query, pageSignal) =>
      getV1CompaniesCompanyIdOrdersOrderIdLocationHistories({
        path: { 'company-id': companyId, 'order-id': orderId },
        query: { 'filter[soft_limit]': false, ...query },
        signal: pageSignal,
      }),
    signal,
  );
}

export function getLocationHistoriesByDriverDayId(
  companyId: string,
  driverDayId: string,
  signal?: AbortSignal,
): Promise<LocationHistoryMinimal[]> {
  return fetchAllLocationHistories(
    (query, pageSignal) =>
      getV1CompaniesCompanyIdDriverDaysDriverDayIdLocationHistories({
        path: { 'company-id': companyId, 'driver-day-id': driverDayId },
        query: { 'filter[soft_limit]': false, ...query },
        signal: pageSignal,
      }),
    signal,
  );
}

export function getLocationHistoriesByMovingSite(
  companyId: string,
  jobId: string,
  movingSiteId: string,
  signal?: AbortSignal,
): Promise<LocationHistoryMinimal[]> {
  return fetchAllLocationHistories(
    (query, pageSignal) =>
      getV1CompaniesCompanyIdJobsJobIdMovingSitesMovingSiteIdLocationHistories({
        path: {
          'company-id': companyId,
          'job-id': jobId,
          'moving-site-id': movingSiteId,
        },
        query,
        signal: pageSignal,
      }),
    signal,
  );
}

export function getMovingSiteLocationHistoriesByOrderId(
  companyId: string,
  orderId: string,
  movingSiteId: string,
  signal?: AbortSignal,
): Promise<LocationHistoryMinimal[]> {
  return fetchAllLocationHistories(
    (query, pageSignal) =>
      getV1CompaniesCompanyIdOrdersOrderIdMovingSitesMovingSiteIdLocationHistories({
        path: {
          'company-id': companyId,
          'order-id': orderId,
          'moving-site-id': movingSiteId,
        },
        query,
        signal: pageSignal,
      }),
    signal,
  );
}
